import { useOfficeSync, useOutlookActions } from "@/pages/taskpane/lib/office/hooks";
import { usePreSelectedResourceStore } from "@/pages/taskpane/shared/stores/preSelectedResourceStore";
import { EPreSelectedResource } from "@/pages/taskpane/shared/types/enums/preSelectedResourceEnum";
import { PreSelectedResourceInfo } from "@/pages/taskpane/shared/types/preSelectedResource";
import { ResourceEntity } from "@features/resources/services/types/resourceEntity";
import { useAppConfigStore, useResourceStore } from "@shared/stores";
import React from "react";
import { EForceOpenOrder } from "./types/enum";
/**
 * Resource Service - Tầng trung gian xử lý logic nghiệp vụ cho resource management
 *
 * Logic nghiệp vụ:
 * 1. Nhận toggle resource từ UI
 * 2. Xử lý điều kiện nghiệp vụ
 * 3. Cập nhật state store nếu thỏa mãn điều kiện
 * 4. Đồng bộ với Outlook API
 * 5. Rollback nếu có lỗi
 */

// Helper function to normalize email format for Outlook
const normalizeEmailForOutlook = (resource: ResourceEntity): string => {
  if (!resource.email && !resource.name && resource.childResources.length === 0) return "unknown@company.com";

  // If already a valid email, return as is
  if (resource.email.includes("@") && resource.email.includes(".")) {
    return resource.email.toLowerCase().trim();
  }

  if (resource.childResources.length > 0) {
    return resource.childResources.map((childResource) => childResource.email.toLowerCase().trim()).join(",");
  }

  // Convert name/path to proper email format
  const cleanName = resource.name
    .toLowerCase()
    .replace(/\s+/g, "") // Remove spaces
    .replace(/[^a-z0-9]/g, "") // Remove special characters
    .replace(/\//g, "") // Remove slashes from paths
    .trim();

  // Ensure we have a valid identifier
  const finalName = cleanName || "resource";

  return `${finalName}@company.com`;
};

export interface ResourceServiceResult {
  success: boolean;
  message: string;
  resource?: ResourceEntity;
}

/**
 * Hook để sử dụng ResourceService
 */
export const useResourceService = () => {
  const {
    selectedResource,
    // ✅ Removed: addSelectedResource, removeSelectedResource, clearSelectedResources
    // We now use OUTLOOK-FIRST approach - only call Outlook API, let useOfficeEvents sync store
  } = useResourceStore();

  const { AddResources, RemoveResources, getSessionId } = useOutlookActions();
  // const { showToast } = useToastStore();
  const { isSyncInProgress } = useOfficeSync();
  const { addSelectedResource, removeSelectedResource, meetingLabel: meetingLabelSelected } = useResourceStore();
  const { addItem } = usePreSelectedResourceStore();
  const { meetingLabel } = useAppConfigStore();

  // Flag để track khi đang xử lý operations để tránh conflict với events
  const [isProcessing, setIsProcessing] = React.useState(false);

  /**
   * Kiểm tra điều kiện nghiệp vụ trước khi xử lý
   */
  const validateResource = (resource: ResourceEntity): ResourceServiceResult => {
    // ✅ Check for sync conflicts first
    if (isSyncInProgress()) {
      console.warn("⚠️ ResourceService: Office sync in progress, deferring operation");
      return {
        success: false,
        message: "Outlook sync in progress, please try again in a moment",
        resource,
      };
    }

    // Kiểm tra resource hợp lệ
    if ((!resource.email && resource.childResources.length === 0) || !resource.name) {
      return {
        success: false,
        message: "Invalid resource: Missing email or name",
        resource,
      };
    }

    // Kiểm tra resource có available không (có thể bỏ qua tùy business logic)
    // if (!resource.IsAvailable) {
    //   return {
    //     success: false,
    //     message: `${resource.Name} is not available`,
    //     resource
    //   };
    // }

    // Kiểm tra giới hạn số lượng resource được chọn (ví dụ: tối đa 5)
    const currentSelectedCount = selectedResource.length;
    const isCurrentlySelected = isResourceSelected(resource);

    // if (!isCurrentlySelected && currentSelectedCount >= 5) {
    //   return {
    //     success: false,
    //     message: "Maximum 5 resources can be selected",
    //     resource
    //   };
    // }

    // Có thể thêm các điều kiện khác tùy theo business logic:
    // - Kiểm tra conflict thời gian
    // - Kiểm tra quyền truy cập
    // - Kiểm tra policy của công ty
    // - etc.

    return {
      success: true,
      message: "Validation passed",
      resource,
    };
  };

  /**
   * Kiểm tra xem resource đã được chọn chưa
   */
  const isResourceSelected = (resource: ResourceEntity): boolean => {
    return selectedResource.some(
      (selected) => selected.email.toLowerCase() === resource.email.toLowerCase() || selected.id === resource.id
    );
  };

  /**
   * Thêm resource mới - OUTLOOK-FIRST approach (no direct store manipulation)
   */
  const addResource = async (resource: ResourceEntity): Promise<ResourceServiceResult> => {
    if (resource.userRestriction) {
      const isForceOpenOrder =
        resource.userRestriction.forceOpenOrder === EForceOpenOrder.NormalOrder ||
        resource.userRestriction.forceOpenOrder === EForceOpenOrder.NormalOrShareOrder ||
        resource.userRestriction.forceOpenOrder === EForceOpenOrder.ShareOrder;
      const sessionIdForOrderForm = await getSessionId(resource.email.toLowerCase());
      if (!sessionIdForOrderForm) {
        const notify = new PreSelectedResourceInfo({
          type: EPreSelectedResource.OpenOderForm,
          message: resource.userRestriction.message,
          resources: [resource],
          forceOpenOrder: resource.userRestriction.forceOpenOrder,
          isResourcePending: isForceOpenOrder,
        });
        addItem(notify);
      }
      const requireMeetingLabel =
        meetingLabel?.isRequired && (meetingLabelSelected === null || meetingLabelSelected === undefined);
      if (requireMeetingLabel) {
        const notify = new PreSelectedResourceInfo({
          type: EPreSelectedResource.MeetingType,
          message: "",
          resources: [resource],
          isResourcePending: true,
        });
        addItem(notify);
      }
      if ((!sessionIdForOrderForm && isForceOpenOrder) || requireMeetingLabel) {
        return {
          success: false,
          message: "Resource pending",
          resource,
        };
      }
    }
    if (meetingLabel?.isRequired && (meetingLabelSelected === null || meetingLabelSelected === undefined)) {
      const notify = new PreSelectedResourceInfo({
        type: EPreSelectedResource.MeetingType,
        message: "",
        resources: [resource],
        isResourcePending: true,
      });
      addItem(notify);
      return {
        success: false,
        message: "Resource pending",
        resource,
      };
    }
    try {
      setIsProcessing(true);

      // ✅ ONLY call Outlook API - let useOfficeEvents handle store updates
      addSelectedResource(resource);
      const outlookSuccess = await AddResources([resource]);

      if (!outlookSuccess) {
        const errorMessage = `Failed to add ${resource.name} to Outlook appointment`;
        // showToast(errorMessage, false);
        return {
          success: false,
          message: errorMessage,
          resource,
        };
      }

      await new Promise((resolve) => setTimeout(resolve, 200));
      const successMessage = `${resource.name} added successfully!`;

      return {
        success: true,
        message: successMessage,
        resource,
      };
    } catch (error) {
      console.error("❌ ResourceService: Add resource failed", error);

      const errorMessage = `Failed to add ${resource.name}`;
      // showToast(errorMessage, false);
      return {
        success: false,
        message: errorMessage,
        resource,
      };
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Xóa resource - OUTLOOK-FIRST approach (no direct store manipulation)
   */
  const removeResource = async (resource: ResourceEntity): Promise<ResourceServiceResult> => {
    // console.log("➖ ResourceService: Removing resource from Outlook (store will sync automatically)", resource.name);

    try {
      setIsProcessing(true);

      // ✅ ONLY call Outlook API - let useOfficeEvents handle store updates
      removeSelectedResource(resource);
      const outlookSuccess = await RemoveResources([resource]);

      if (!outlookSuccess) {
        const errorMessage = `Failed to remove ${resource.name} from Outlook appointment`;
        // showToast(errorMessage, false);
        return {
          success: false,
          message: errorMessage,
          resource,
        };
      }

      await new Promise((resolve) => setTimeout(resolve, 200));
      const successMessage = `${resource.name} removed successfully!`;
      return {
        success: true,
        message: successMessage,
        resource,
      };
    } catch (error) {
      console.error("❌ ResourceService: Remove resource failed", error);

      const errorMessage = `Failed to remove ${resource.name}`;
      // showToast(errorMessage, false);
      return {
        success: false,
        message: errorMessage,
        resource,
      };
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Toggle resource selection với logic nghiệp vụ
   */
  const toggleResource = async (resource: ResourceEntity, isSelected?: boolean): Promise<ResourceServiceResult> => {
    try {
      // console.log("🔄 ResourceService: Toggle resource called >>>>>>>>>>>>>>>>>", resource.name);

      // Normalize email format để ensure consistency
      resource.email = normalizeEmailForOutlook(resource);
      resource.location = resource.location || resource.name || "Meeting Room";
      // console.log(" normalizedResource >>>>>>>>>>>>>>>>>", normalizedResource);

      // Kiểm tra điều kiện nghiệp vụ trước khi xử lý
      const validationResult = validateResource(resource);
      if (!validationResult.success) {
        // showToast(validationResult.message, false);
        return validationResult;
      }

      // Kiểm tra xem resource đã được chọn chưa
      const isCurrentlySelected = isSelected ?? isResourceSelected(resource);

      if (isCurrentlySelected) {
        return await removeResource(resource);
      } else {
        return await addResource(resource);
      }
    } catch (error) {
      console.error("❌ ResourceService: Toggle resource failed", error);
      const errorMessage = `Failed to process resource: ${resource.name}`;
      // showToast(errorMessage, false);
      return {
        success: false,
        message: errorMessage,
        resource,
      };
    }
  };

  /**
   * Batch operation: Thêm nhiều resources cùng lúc
   */
  const addMultipleResources = async (resources: ResourceEntity[]): Promise<ResourceServiceResult[]> => {
    // console.log("📦 ResourceService: Adding multiple resources", resources.length);

    const results: ResourceServiceResult[] = [];

    for (const resource of resources) {
      if (!isResourceSelected(resource)) {
        const result = await addResource(resource);
        results.push(result);
      }
    }

    return results;
  };

  /**
   * Batch operation: Xóa nhiều resources cùng lúc
   */
  const removeMultipleResources = async (resources: ResourceEntity[]): Promise<ResourceServiceResult[]> => {
    // console.log("📦 ResourceService: Removing multiple resources", resources.length);

    const results: ResourceServiceResult[] = [];

    for (const resource of resources) {
      if (isResourceSelected(resource)) {
        const result = await removeResource(resource);
        results.push(result);
      }
    }

    return results;
  };

  /**
   * Clear all selected resources - OUTLOOK-FIRST approach
   */
  const clearAllResources = async (): Promise<ResourceServiceResult> => {
    // console.log("🧹 ResourceService: Clearing all resources from Outlook (store will sync automatically)");

    try {
      setIsProcessing(true);
      const selectedResources = [...selectedResource];

      if (selectedResources.length === 0) {
        return {
          success: true,
          message: "No resources to clear",
        };
      }

      // ✅ ONLY call Outlook API - let useOfficeEvents handle store updates
      const outlookSuccess = await RemoveResources(selectedResources);

      if (!outlookSuccess) {
        throw new Error("Failed to remove resources from Outlook");
      }

      // ✅ Wait for useOfficeEvents to sync the change (no manual store updates)
      await new Promise((resolve) => setTimeout(resolve, 200));

      const successMessage = `Cleared ${selectedResources.length} resources successfully!`;

      return {
        success: true,
        message: successMessage,
      };
    } catch (error) {
      console.error("❌ ResourceService: Clear all failed", error);
      const errorMessage = "Failed to clear all resources";
      // showToast(errorMessage, false);

      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    toggleResource,
    addMultipleResources,
    removeMultipleResources,
    clearAllResources,
    isResourceSelected,
    isProcessing, // Export processing state
  };
};
